import { useCallback } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { toaster } from 'components/toaster/toaster';
import {
  PosVendor,
  useHydrateCartMutation,
  useRehydrateCartMutation,
} from 'generated/graphql-gateway';
import { useGetComboLazyQuery } from 'generated/sanity-graphql';
import { hydrateLegacyCart } from 'hooks/hydrateEntries';
import { useCart } from 'hooks/use-cart';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useServiceModeContext } from 'state/service-mode';
import { frontendToBackendCartEntryMap } from 'utils/cart';
import { CartEntryType } from 'utils/cart/types';
import logger from 'utils/logger';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';

import {
  IPrepareCartEntries,
  IPrepareReorderCartEntries,
  IUseCartHydrationParams,
  IUseCartHydrationReturnType,
} from './types';
import { rehydrateEntries } from './utils';

/**
 * Custom hook that manages cart hydration and rehydration during store changes.
 *
 * - If switching from One Menu (1M) to a Legacy store, or between Legacy stores,
 *   the hook triggers rehydration.
 *   Rehydration retrieves the proper vendor configuration for the new store and
 *   fills cart entries with the missing necessary Sanity data to reprice them.
 *
 * - If switching from a Legacy store to a One Menu store, the hook performs a Full Cart Hydration.
 *   Hydration converts the legacy cart into minimal cart entries,
 *   and hydrates them for One Menu.
 *
 * - Additionally, if there are pending minimal cart entries for a One Menu store and the new selected store is Legacy,
 *   it hydrates them and retrieves missing Sanity data as needed.
 *
 * - If the process fails at any point, it resets both the legacy and minimal carts
 *   and redirects the user back to the main menu screen.
 *
 * @param {IUseCartHydrationParams} params - Parameters for the hook.
 * @param {ILegacyCartEntry[]} [params.cartEntries] - Current full cart entries (Legacy mode).
 * @param {IMinimalCartEntry[]} [params.minimalCartEntries] - Current minimal cart entries (One Menu mode).
 *
 * @returns {IUseCartHydrationReturnType} An object exposing the `prepareCartEntries` function
 * to be used when handling store changes.
 */

let isStoreChangeHydrationInProgress = false;
let isReorderingDehydrationInProgress = false;

export const isHydrationOrDehydrationInProgress =
  isStoreChangeHydrationInProgress || isReorderingDehydrationInProgress;

export const useCartHydration = ({
  cartEntries = [],
  minimalCartEntries = [],
}: IUseCartHydrationParams): IUseCartHydrationReturnType => {
  const [hydrateCart] = useHydrateCartMutation();
  const [rehydrateCart] = useRehydrateCartMutation();
  const [fetchCombo] = useGetComboLazyQuery();

  const { serviceMode } = useServiceModeContext();
  const { emptyMinimalCart } = useMinimalCart();
  const { removeAllFromCart, removeFromCart } = useCart();

  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const dispatch = useAppDispatch();
  const router = useRouter();

  const triggerViewAvailableOffersToast = useCallback(() => {
    toaster.viewAvailableOffersToast(
      t('viewAvailableOffersToast'),
      t('viewAvailableOffersToastLink')
    );
  }, []);

  /**
   * Handles cart rehydration or hydration when switching between stores.
   *
   * - If switching from One Menu (1M) to a Legacy store or between Legacy stores,
   *   performs rehydration to fetch necessary vendor configurations and Sanity data.
   *
   * - If switching from a Legacy store to a One Menu store, performs full hydration
   *   by mapping the legacy cart to minimal cart entries and hydrating them for One Menu.
   *
   * - If there are existing minimal cart entries when switching to a Legacy store,
   *   hydrates them with Sanity data as needed.
   *
   * - On any failure, resets the cart and navigates back to the main menu.
   *
   * @param {IPrepareCartEntries} params - Parameters for cart preparation.
   * @param {IRestaurant} params.store - The current store associated with the cart.
   * @param {IRestaurant} params.newStore - The new store being selected.
   * @param {'pickup' | 'delivery'} params.requestedServiceMode - The new requested service mode.
   *
   * @returns {Promise<void>} A promise that resolves when the cart has been handled.
   */
  const prepareCartEntries = useCallback(
    async ({ store, newStore, requestedServiceMode }: IPrepareCartEntries) => {
      try {
        if (isStoreChangeHydrationInProgress) {
          return;
        }
        isStoreChangeHydrationInProgress = true;

        const isCurrentStoreOneMenu = !!store.isOneMenu;
        const isNewStoreOneMenu = !!newStore.isOneMenu;

        const isSameStore = store.number === newStore.number;
        const isSamePosVendor =
          store.pos.vendor.toLocaleLowerCase() === newStore.pos.vendor.toLocaleLowerCase();
        const isSameServiceMode = serviceMode === requestedServiceMode;

        const newStoreId = newStore.number ?? '';

        const switchOneMenuToLegacy = isCurrentStoreOneMenu && !isNewStoreOneMenu;
        const switchLegacyToLegacy = !isCurrentStoreOneMenu && !isNewStoreOneMenu;

        // This means we already have all the necessary data to reprice
        const cartEntriesHaveFullVendorConfigs = cartEntries.every(
          cartEntry =>
            !!cartEntry.vendorConfigs &&
            Object.values(cartEntry.vendorConfigs).every(value => !!value)
        );

        const shouldSkipRehydration =
          (isSameServiceMode && (isSameStore || isSamePosVendor)) ||
          cartEntriesHaveFullVendorConfigs;

        const shouldRehydrate =
          cartEntries.length &&
          (switchOneMenuToLegacy || (switchLegacyToLegacy && !shouldSkipRehydration));

        const shouldHydrateFullCart =
          cartEntries.length && !isCurrentStoreOneMenu && isNewStoreOneMenu;

        if (shouldRehydrate) {
          // Remove in advance any applied offer to avoid offer combo rehydration errors
          const offerComboEntry = cartEntries.find(
            cartEntry => cartEntry.type === CartEntryType.offerCombo
          );

          if (offerComboEntry) {
            // This will remove both the cart entry and the applied offer
            removeFromCart({ cartId: offerComboEntry.cartId });
            triggerViewAvailableOffersToast();
          }

          const entriesToRehydrate = cartEntries.filter(
            cartEntry => cartEntry.type !== CartEntryType.offerCombo
          );

          const entries = await rehydrateEntries({
            cartEntries: entriesToRehydrate,
            store,
            newStore,
            serviceMode: requestedServiceMode,
            rehydrateCart,
            fetchCombo,
            cmsOffers,
          });

          if (entries?.length) {
            dispatch(actions.ordering.repriceCartEntries(entries));
          }

          // For Rehydration, remove in advance any generated cart entry for a discount offer, but not the applied offer.
          dispatch(actions.ordering.removeCartEntries(new Set([DISCOUNT_OFFER_CART_ID])));
        } else if (shouldHydrateFullCart) {
          const entries = await hydrateLegacyCart({
            legacyCartEntries: cartEntries,
            appliedOffers,
            cmsOffers,
            serviceMode: requestedServiceMode,
            storeId: newStoreId,
            hydrateCart,
          });

          if (entries?.length) {
            dispatch(actions.ordering.repriceCartEntries(entries));
          }
        }
      } catch (error) {
        logger.error({
          message: 'Cart Hydration/Rehydration failed at store change',
          storeId: store.number,
          newStoreId: newStore.number,
          cartEntries: JSON.stringify(cartEntries),
          error: JSON.stringify(error),
        });

        // If anything on this process fails, then reset the carts and redirect to the main menu screen.
        removeAllFromCart(cartEntries);
        emptyMinimalCart();

        if (router.canDismiss()) {
          router.dismissAll();
        }
        router.replace(routes.menu);
      } finally {
        isStoreChangeHydrationInProgress = false;
      }
    },
    [
      serviceMode,
      cartEntries,
      appliedOffers,
      cmsOffers,
      rehydrateCart,
      fetchCombo,
      dispatch,
      removeFromCart,
      triggerViewAvailableOffersToast,
      hydrateCart,
      removeAllFromCart,
      emptyMinimalCart,
      router,
    ]
  );

  /**
   * Prepares cart entries for reorder flow during store switch.
   *
   * - Rehydrates reorder entries entries if switching from One Menu to Legacy, or between Legacy stores,
   *   and if vendor config data is incomplete or store/pos/service mode has changed.
   *
   * - If nothing needs to change or hydrate, returns the original cart entries.
   *
   * - If rehydration fails, logs the error and returns the original cart entries as fallback.
   *
   * @param {IPrepareReorderCartEntries} params - Parameters for reorder cart preparation.
   * @param {IReorderableOrder} params.order - The original reorderable order containing cart and store data.
   * @param {IRestaurant} params.newStore - The new store where the reorder is attempted.
   *
   * @returns {Promise<IBackendCartEntries[]>} A promise that resolves to backend-compatible cart entries.
   */
  const prepareReorderCartEntries = useCallback(
    async ({ order, newStore }: IPrepareReorderCartEntries): Promise<IBackendCartEntries[]> => {
      const originalStoreId = order.cart.storeId.toString();
      const originalPosVendor = order.cart.posVendor as PosVendor;
      const originalServiceMode = order.cart.serviceMode;

      const cartEntries = order?.cart?.cartEntries ?? [];

      try {
        if (isReorderingDehydrationInProgress) {
          return cartEntries;
        }
        const isOriginalStoreOneMenu = originalPosVendor === PosVendor.BRINK;
        const isNewStoreOneMenu = !!newStore.isOneMenu;

        const isSameStore = order.cart.storeId.toString() === newStore.number;

        const isSamePosVendor =
          originalPosVendor.toString().toLocaleLowerCase() ===
          newStore.pos.vendor.toLocaleLowerCase();

        const isSameServiceMode = originalServiceMode === serviceMode;

        const switchOneMenuToLegacy = isOriginalStoreOneMenu && !isNewStoreOneMenu;
        const switchLegacyToLegacy = !isOriginalStoreOneMenu && !isNewStoreOneMenu;

        // This means we already have all the necessary data to reprice
        const cartEntriesHaveFullVendorConfigs = cartEntries.every(
          cartEntry =>
            !!cartEntry.vendorConfigs &&
            Object.values(cartEntry.vendorConfigs).every(value => !!value)
        );

        // @TODO BKPE-23277: Temporal solution for OfferCombo reordering Hydration/Rehydration.
        // We should skip for now hydration/rehydration for OfferCombo because we have a known issue with hydration.
        // useReorderableOrders will run makeReorderableOrder and will convert the OfferCombo to ALC item.
        // This way the cart entry will be available for reordering anyways.
        const orderContainsOfferCombo = cartEntries.some(
          cartEntry => cartEntry.type === CartEntryType.offerCombo
        );

        const shouldSkipRehydration =
          orderContainsOfferCombo ||
          (isSameServiceMode && (isSameStore || isSamePosVendor)) ||
          cartEntriesHaveFullVendorConfigs;

        const shouldRehydrate =
          cartEntries.length &&
          !shouldSkipRehydration &&
          (switchOneMenuToLegacy || switchLegacyToLegacy);

        const store = {
          number: originalStoreId,
          pos: {
            vendor: originalPosVendor,
          },
          phoneNumber: '',
          physicalAddress: {},
        } as IRestaurant;

        const mappedCartEntries = cartEntries.map(cartEntry => mapBackendToFrontend(cartEntry));

        if (shouldRehydrate) {
          isReorderingDehydrationInProgress = true;
          const rehydratedEntries = await rehydrateEntries({
            cartEntries: mappedCartEntries,
            store,
            newStore,
            serviceMode,
            rehydrateCart,
            fetchCombo,
            cmsOffers,
          });

          const backendCartEntries = rehydratedEntries.map(cartEntry =>
            frontendToBackendCartEntryMap(cartEntry)
          ) as IBackendCartEntries[];

          return backendCartEntries;
        }

        return cartEntries;
      } catch (error) {
        logger.error({
          message: 'Reorderable Cart Entries Hydration/Rehydration failed',
          storeId: originalStoreId,
          newStoreId: newStore.number,
          cartEntries: JSON.stringify(cartEntries),
          error: JSON.stringify(error),
        });

        return cartEntries;
      } finally {
        isReorderingDehydrationInProgress = false;
      }
    },
    [cmsOffers, fetchCombo, rehydrateCart, serviceMode]
  );

  return {
    prepareCartEntries,
    prepareReorderCartEntries,
    isStoreChangeHydrationInProgress,
  };
};
